import {
    LayoutDashboard, Building2, Users, GitPullRequest, ClipboardList,
    FileText, Sparkles, Mail, CheckSquare, BarChart3, Settings,
    Package, Zap, ListChecks, Inbox, CalendarDays, HeartPulse, Lock, Lightbulb,
    Workflow, Rocket, Library, MessageSquareQuote, BookOpen, Globe,
    Briefcase, Database, Bot, FileSearch, Search, ShieldCheck, Shield, Layers,
    ShoppingCart, Megaphone, Headphones, Cog, Monitor, UserCheck, Scale, DollarSign,
    Trophy, Target, CalendarCheck, KanbanSquare
} from 'lucide-react';

// ─── Universal items (shown in every pillar's sidebar) ─────────────────────

export const UNIVERSAL_TOP = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    { label: 'Inbox', icon: Inbox, path: '/inbox' },
    { label: 'Tasks', icon: CheckSquare, path: '/tasks', badge: 'tasks' },
    { label: 'Wiki', icon: BookOpen, path: '/wiki', feature: 'wiki' },
];

export const UNIVERSAL_AI = {
    id: 'ai-tools',
    label: 'AI Tools',
    icon: Sparkles,
    items: [
        { label: 'AI Assistant', icon: Sparkles, path: '/ai-assistant', ai: true, feature: 'ai_assistant' },
        { label: 'AI Heartbeat', icon: HeartPulse, path: '/ai-heartbeat', adminOnly: true, feature: 'ai_heartbeat' },
        { label: 'AI Reviews', icon: ShieldCheck, path: '/ai-reviews', badge: 'reviews', feature: 'agentic_personalization' },
        { label: 'AI Agents', icon: Bot, path: '/agents', agentDeveloper: true },
    ],
};


export const UNIVERSAL_SUPPORT = {
    id: 'support',
    label: 'Support',
    icon: Lightbulb,
    items: [
        { label: 'Help Guide', icon: BookOpen, path: '/help' },
        { label: 'Feature Requests', icon: Lightbulb, path: '/feature-requests' },
        { label: 'Release Notes', icon: Rocket, path: '/release-notes' },
    ],
};

export const BOTTOM_PINNED = [
    { label: 'Settings', icon: Settings, path: '/settings' },
    { label: 'Admin', icon: ShieldCheck, path: '/admin', adminOnly: true },
    { label: 'Platform Admin', icon: Shield, path: '/super-admin', superAdminOnly: true },
];

// ─── Pillar definitions ────────────────────────────────────────────────────

export const PILLAR_DEFS = [
    {
        id: 'marketing',
        label: 'Marketing',
        icon: Megaphone,
        comingSoon: false,
        moduleKey: null,
        defaultPath: '/campaigns',
        pathPrefixes: ['/campaigns', '/events', '/forms', '/form-flows', '/bookings', '/email-sequences', '/sites', '/content-library', '/testimonials', '/affiliates', '/list-management'],
        sections: [
            {
                id: 'marketing-outreach',
                label: 'Outreach',
                icon: Mail,
                items: [
                    { label: 'Campaigns', icon: Workflow, path: '/campaigns', feature: 'campaigns' },
                    { label: 'Events', icon: CalendarDays, path: '/events', feature: 'events' },
                    { label: 'Forms', icon: ClipboardList, path: '/forms', feature: 'forms' },
                    { label: 'Form Flows', icon: Workflow, path: '/form-flows', feature: 'forms' },
                    { label: 'Bookings', icon: CalendarCheck, path: '/bookings', feature: 'forms' },
                    { label: 'Sites', icon: Globe, path: '/sites', feature: 'sites' },
                    { label: 'Content Library', icon: Library, path: '/content-library' },
                    { label: 'Testimonials', icon: MessageSquareQuote, path: '/testimonials' },
                    { label: 'Affiliates', icon: Users, path: '/affiliates', feature: 'affiliates' },
                ],
            },
            {
                id: 'marketing-insights',
                label: 'Insights',
                icon: BarChart3,
                items: [
                    { label: 'Activities', icon: CheckSquare, path: '/activities' },
                    { label: 'Reports', icon: BarChart3, path: '/reports' },
                    { label: 'List Management', icon: ListChecks, path: '/list-management', adminOnly: true, feature: 'list_management' },
                ],
            },
        ],
    },
    {
        id: 'sales',
        label: 'Sales',
        icon: ShoppingCart,
        comingSoon: false,
        moduleKey: null,
        defaultPath: '/pipeline',
        pathPrefixes: ['/companies', '/contacts', '/pipeline', '/lead-pipeline', '/deals', '/deal-rooms', '/deal-registration', '/products', '/proposals', '/booking-links', '/playbooks', '/meeting-sessions', '/desktop-app', '/practice', '/role-play', '/micro-practice', '/leaderboard'],
        sections: [
            {
                id: 'sales-crm',
                label: 'CRM',
                icon: Building2,
                items: [
                    { label: 'Companies', icon: Building2, path: '/companies' },
                    { label: 'Contacts', icon: Users, path: '/contacts' },
                    { label: 'Pipeline', icon: GitPullRequest, path: '/pipeline' },
                    { label: 'Lead Pipeline', icon: KanbanSquare, path: '/lead-pipeline' },
                    { label: 'Deal Rooms', icon: Layers, path: '/deal-rooms', feature: 'deal_rooms' },
                    { label: 'Products', icon: Package, path: '/products' },
                    { label: 'Proposals', icon: FileText, path: '/proposals' },
                    { label: 'Booking Links', icon: CalendarDays, path: '/booking-links', feature: 'booking_links' },
                ],
            },
            {
                id: 'sales-meetings',
                label: 'Meeting Copilot',
                icon: Briefcase,
                items: [
                    { label: 'Playbooks', icon: BookOpen, path: '/playbooks', adminOnly: true },
                    { label: 'Meeting Sessions', icon: ClipboardList, path: '/meeting-sessions' },
                    { label: 'Desktop App', icon: Monitor, path: '/desktop-app' },
                ],
            },
            {
                id: 'sales-practice',
                label: 'Practice',
                icon: Target,
                items: [
                    { label: 'Role Play', icon: Sparkles, path: '/practice/role-play' },
                    { label: 'Micro-Practice', icon: Zap, path: '/micro-practice' },
                    { label: 'Leaderboard', icon: Trophy, path: '/leaderboard' },
                    { label: 'Avatar Library', icon: Users, path: '/admin/role-play/avatars', adminOnly: true },
                ],
            },
            {
                id: 'sales-insights',
                label: 'Insights',
                icon: BarChart3,
                items: [
                    { label: 'Activities', icon: CheckSquare, path: '/activities' },
                    { label: 'Reports', icon: BarChart3, path: '/reports' },
                ],
            },
        ],
    },
    {
        id: 'customer-service',
        label: 'Service',
        icon: Headphones,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Manage customer support tickets, knowledge base, live chat, and customer satisfaction tracking.',
    },
    {
        id: 'operations',
        label: 'Operations',
        icon: Cog,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Streamline workflows, project management, inventory, and operational processes.',
    },
    {
        id: 'it',
        label: 'IT',
        icon: Monitor,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'IT asset management, helpdesk, infrastructure monitoring, and security compliance.',
    },
    {
        id: 'hr',
        label: 'HR',
        icon: UserCheck,
        comingSoon: false,
        moduleKey: 'ats',
        defaultPath: '/recruitment',
        pathPrefixes: ['/recruitment'],
        sections: [
            {
                id: 'hr-recruitment',
                label: 'Recruitment',
                icon: Briefcase,
                items: [
                    { label: 'Job Orders', icon: ClipboardList, path: '/recruitment/jobs' },
                    { label: 'Talent Pool', icon: Database, path: '/recruitment/talent-pool' },
                    { label: 'Activation', icon: Zap, path: '/recruitment/activation' },
                    { label: 'Fast Matching', icon: Search, path: '/recruitment/matching' },
                    { label: 'CV Review', icon: FileSearch, path: '/recruitment/cv-review' },
                ],
            },
            {
                id: 'hr-ai',
                label: 'AI Agents',
                icon: Bot,
                items: [
                    { label: 'AI Actions', icon: Bot, path: '/recruitment/agent-queue' },
                ],
            },
            {
                id: 'hr-config',
                label: 'Configuration',
                icon: Settings,
                items: [
                    { label: 'ATS Settings', icon: Settings, path: '/recruitment/settings', adminOnly: true },
                    { label: 'Reports', icon: BarChart3, path: '/recruitment/reports' },
                ],
            },
        ],
    },
    {
        id: 'legal',
        label: 'Legal',
        icon: Scale,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Contract management, compliance tracking, legal document templates, and approval workflows.',
    },
    {
        id: 'finance',
        label: 'Finance',
        icon: DollarSign,
        comingSoon: true,
        moduleKey: null,
        defaultPath: null,
        pathPrefixes: [],
        sections: [],
        description: 'Invoicing, expense management, financial reporting, and budget tracking.',
    },
];

// Build a flat list of all universal group objects for the sidebar
export const UNIVERSAL_GROUPS = [UNIVERSAL_AI, UNIVERSAL_SUPPORT];
